import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import Paginator from '../components/Paginator';
import ConfirmDialog from '../components/ConfirmDialog';
import styles from './AdminCoursesTab.module.css';

export default function AdminCoursesTab({ onToast }) {
  const { token } = useAuth();
  const { t } = useLang();

  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  // Filters & Sorting state
  const [filterAccess, setFilterAccess] = useState('all');
  const [filterPublish, setFilterPublish] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    onConfirm: null,
    type: 'warning'
  });

  async function loadCourses() {
    try {
      const res = await fetch(`${API}/api/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCourses(await res.json());
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error') || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCourses();
  }, []); // eslint-disable-line

  async function togglePublish(courseId, currentPublished) {
    if (toggling[courseId]) return;
    setToggling(prev => ({ ...prev, [courseId]: true }));
    
    try {
      const res = await fetch(`${API}/api/courses/${courseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          isPublished: !currentPublished
        })
      });

      if (res.ok) {
        onToast(t('common.success') || 'Course updated successfully');
        await loadCourses();
      } else {
        const errorData = await res.json();
        onToast(errorData.error || t('common.error'));
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error'));
    } finally {
      setToggling(prev => ({ ...prev, [courseId]: false }));
    }
  }

  function triggerDelete(courseId, title) {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Course',
      message: `Are you absolutely sure you want to delete "${title}"? All modules, lessons, attachments, certificates, and learner progress for this course will be deleted permanently.`,
      confirmText: 'Delete',
      cancelText: t('common.cancel', 'Cancel'),
      type: 'danger',
      onConfirm: () => confirmDeleteCourse(courseId)
    });
  }

  async function confirmDeleteCourse(courseId) {
    try {
      const res = await fetch(`${API}/api/courses/${courseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        onToast(t('common.success') || 'Course deleted successfully');
        await loadCourses();
      } else {
        const errorData = await res.json();
        onToast(errorData.error || t('common.error'));
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error'));
    } finally {
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }

  // Filter logic
  const filtered = courses.filter(c => {
    const matchesSearch = !search ||
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.description?.toLowerCase().includes(search.toLowerCase());
      
    const matchesAccess = filterAccess === 'all' || c.accessType === filterAccess;
    
    const matchesPublish = filterPublish === 'all' || 
      (filterPublish === 'published' ? c.isPublished === true : c.isPublished === false);

    return matchesSearch && matchesAccess && matchesPublish;
  });

  // Sort logic
  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortKey) {
      case 'title':
        aVal = (a.title || '').toLowerCase();
        bVal = (b.title || '').toLowerCase();
        break;
      case 'accessType':
        aVal = a.accessType || '';
        bVal = b.accessType || '';
        break;
      case 'totalLessons':
        aVal = a.totalLessons || 0;
        bVal = b.totalLessons || 0;
        break;
      case 'price':
        aVal = a.price || 0;
        bVal = b.price || 0;
        break;
      case 'createdAt':
      default:
        aVal = new Date(a.createdAt || 0).getTime();
        bVal = new Date(b.createdAt || 0).getTime();
        break;
    }

    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate logic
  const paginated = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  function renderSortIndicator(key) {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <div className="flex-col">
      <div className="card">
        <div className="card-header">
          <div className="card-title">📚 Course Curriculums</div>
        </div>
        <div className="card-body" style={{ paddingBottom: 16 }}>
          <div className={styles.searchBar}>
            <input 
              value={search} 
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} 
              placeholder="Search by course title or description..." 
              className={styles.searchInput}
            />
            <button className="btn btn-ghost btn-sm" onClick={loadCourses}>🔄 Refresh</button>
          </div>
          
          <div className={styles.filterBar}>
            <select
              className={styles.filterSelect}
              value={filterAccess}
              onChange={e => { setFilterAccess(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Access Types</option>
              <option value="free">Free</option>
              <option value="members">Members only</option>
              <option value="premium">Premium</option>
              <option value="level">Level Locked</option>
            </select>

            <select
              className={styles.filterSelect}
              value={filterPublish}
              onChange={e => { setFilterPublish(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All Statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>{t('common.loading')}</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.sortHeader} onClick={() => handleSort('title')}>
                      Course Curriculum{renderSortIndicator('title')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('accessType')}>
                      Access Type{renderSortIndicator('accessType')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('price')}>
                      Access Price{renderSortIndicator('price')}
                    </th>
                    <th className={styles.sortHeader} onClick={() => handleSort('totalLessons')}>
                      Total Lessons{renderSortIndicator('totalLessons')}
                    </th>
                    <th>Status</th>
                    <th className={styles.sortHeader} onClick={() => handleSort('createdAt')}>
                      Created Date{renderSortIndicator('createdAt')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <img 
                            src={c.coverImageUrl ? `${API}${c.coverImageUrl}` : `https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=120&auto=format&fit=crop&q=60`} 
                            alt="cover" 
                            style={{ width: 48, height: 32, borderRadius: 4, objectFit: 'cover', background: 'var(--surface2)' }} 
                          />
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.title}</div>
                            <div className="text-muted" style={{ fontSize: 11 }} title={c.description || 'No description provided'}>
                              {c.description 
                                ? (c.description.length > 75 ? c.description.substring(0, 72) + '...' : c.description) 
                                : 'No description provided'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={styles.badge}>
                          {c.accessType === 'free' && 'Free'}
                          {c.accessType === 'members' && 'Members only'}
                          {c.accessType === 'premium' && 'Premium'}
                          {c.accessType === 'level' && `Level ${c.requiredLevel || 0}+`}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, color: c.accessType === 'premium' ? 'var(--accent3)' : 'var(--text3)' }}>
                          {c.accessType === 'premium' ? `฿${(c.price || 0).toLocaleString()}` : '—'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 500 }}>{c.totalLessons || 0} lessons</span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${c.isPublished ? styles.published : styles.draft}`}>
                          <span className={styles.statusDot} />
                          {c.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button 
                            className="btn btn-ghost btn-sm"
                            style={{ color: c.isPublished ? 'var(--accent2)' : 'var(--accent3)' }}
                            onClick={() => togglePublish(c.id, c.isPublished)}
                            disabled={toggling[c.id]}
                          >
                            {c.isPublished ? 'Unpublish' : 'Publish'}
                          </button>
                          <button 
                            className="btn btn-danger btn-sm" 
                            onClick={() => triggerDelete(c.id, c.title)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>
                        No courses found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Paginator
              totalItems={sorted.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        type={confirmDialog.type}
      />
    </div>
  );
}
